import { z } from 'zod';

import { ANALOG_BANDWIDTH_OPTIONS, DIGITAL_BANDWIDTH_OPTIONS } from '../constants/suppressions.js';
import { t } from '../i18n/validation.js';
import { SignalType } from '../types';

const MIN_BANDWIDTH_MHZ = Math.min(...DIGITAL_BANDWIDTH_OPTIONS, ...ANALOG_BANDWIDTH_OPTIONS);

const bandwidthSchema = z.coerce
  .number({ error: t('validation.suppression.bandwidthInvalid') })
  .refine(Number.isFinite, t('validation.suppression.bandwidthInvalid'))
  .refine((value) => value >= MIN_BANDWIDTH_MHZ, {
    message: t('validation.suppression.bandwidthMin', { min: MIN_BANDWIDTH_MHZ }),
  });

export const buildManualSuppressionSchema = (
  minAllowedMhz: number | null,
  maxAllowedMhz: number | null,
  maxSuppressionTimeSec?: number | null,
) => {
  const finiteNumber = (message: string) =>
    z.coerce.number({ error: message }).refine(Number.isFinite, message);

  let frequencySchema = finiteNumber(t('validation.frequency.frequencyInvalid'));

  if (minAllowedMhz !== null && minAllowedMhz > 0) {
    frequencySchema = frequencySchema.refine((value: number) => value >= minAllowedMhz, {
      message: t('validation.frequency.frequencyMin', { min: minAllowedMhz }),
    });
  }

  if (maxAllowedMhz !== null && maxAllowedMhz > 0) {
    frequencySchema = frequencySchema.refine((value: number) => value <= maxAllowedMhz, {
      message: t('validation.frequency.frequencyMax', { max: maxAllowedMhz }),
    });
  }

  const durationSchema = z
    .string()
    .min(1, t('validation.suppression.durationRequired'))
    .refine((value) => /^\d+$/.test(value), {
      message: t('validation.suppression.durationInvalid'),
    })
    .refine((value) => Number.parseInt(value, 10) >= 1, {
      message: t('validation.suppression.durationMin'),
    });

  return z.object({
    id: z.string(),
    type: z.nativeEnum(SignalType),
    frequency: frequencySchema,
    bandwidth: bandwidthSchema,
    duration:
      maxSuppressionTimeSec != null && maxSuppressionTimeSec > 0
        ? durationSchema.refine((value) => Number.parseInt(value, 10) <= maxSuppressionTimeSec, {
            message: t('validation.suppression.durationMax', { max: maxSuppressionTimeSec }),
          })
        : durationSchema,
  });
};

export const buildDetectionGroupSuppressionSchema = (maxSuppressionTime: number) =>
  z.object({
    duration: z
      .string()
      .min(1, t('validation.suppression.durationRequired'))
      .regex(/^\d+$/, t('validation.suppression.durationInvalid'))
      .refine((value) => Number.parseInt(value, 10) >= 1, {
        message: t('validation.suppression.durationMin'),
      })
      .refine((value) => Number.parseInt(value, 10) <= maxSuppressionTime, {
        message: t('validation.suppression.durationMax', { max: maxSuppressionTime }),
      }),
    bandwidth: bandwidthSchema,
    isPriority: z.boolean(),
  });

export type DetectionGroupSuppressionFormValues = z.infer<
  ReturnType<typeof buildDetectionGroupSuppressionSchema>
>;

export type ManualSuppressionFormRow = z.infer<ReturnType<typeof buildManualSuppressionSchema>>;
